import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  Card,
  CardMedia,
  CardActions,
  CardContent,
} from '@mui/material';
import {
  CameraAlt as CameraIcon,
  PhotoCamera as PhotoCameraIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import {
  FormCard,
  BackButton,
  NextButton,
  PrimaryButton,
  SecondaryButton,
} from './UI';

interface PaymentPhotoStepProps {
  initialPhoto?: File;
  onPhotoChange: (photo: File | undefined) => void;
  onNext: () => void;
  onBack: () => void;
  showNavigationButtons?: boolean;
}

export const PaymentPhotoStep: React.FC<PaymentPhotoStepProps> = ({
  initialPhoto,
  onPhotoChange,
  onNext,
  onBack,
  showNavigationButtons = true,
}) => {
  const [photo, setPhoto] = useState<File | undefined>(initialPhoto);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // Crea preview URL quando la foto cambia
  React.useEffect(() => {
    if (photo) {
      const url = URL.createObjectURL(photo);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setPreviewUrl(null);
    }
  }, [photo]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.type.startsWith('image/')) {
        setPhoto(file);
        onPhotoChange(file);
        setError(null);
      } else {
        setError('Seleziona un file immagine valido');
      }
    }
  };

  const handleCameraOpen = async () => {
    try {
      setError(null);
      
      // Verifica se la fotocamera è disponibile
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setError('La fotocamera non è supportata su questo dispositivo');
        return;
      }
      
      // Richiedi permessi esplicitamente
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });
      
      streamRef.current = stream;
      setIsCameraOpen(true);
      
      // Aspetta che il video sia pronto
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      }, 100);
    } catch (err: any) {
      console.error('Camera error:', err);
      
      if (err.name === 'NotAllowedError') {
        setError('Permessi fotocamera negati. Abilita i permessi nelle impostazioni del browser.');
      } else if (err.name === 'NotFoundError') {
        setError('Nessuna fotocamera trovata su questo dispositivo.');
      } else if (err.name === 'NotSupportedError') {
        setError('La fotocamera non è supportata su questo dispositivo.');
      } else {
        setError('Impossibile accedere alla fotocamera. Verifica i permessi.');
      }
    }
  };

  const handleCapture = () => {
    if (!videoRef.current || !canvasRef.current) return;

    setIsCapturing(true);
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    if (!context) {
      setError('Errore durante la cattura dell\'immagine');
      setIsCapturing(false);
      return;
    }

    // Imposta le dimensioni del canvas
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Disegna il frame del video sul canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Converti in blob
    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], `payment_${Date.now()}.jpg`, {
          type: 'image/jpeg'
        });
        
        setPhoto(file);
        onPhotoChange(file);
        handleCameraClose();
        setError(null);
      } else {
        setError('Errore durante il salvataggio dell\'immagine');
      }
      setIsCapturing(false);
    }, 'image/jpeg', 0.8);
  };

  const handleCameraClose = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setIsCameraOpen(false);
  };

  const handleDeletePhoto = () => {
    setPhoto(undefined);
    onPhotoChange(undefined);
    setError(null);
  };

  const handleRetakePhoto = () => {
    handleDeletePhoto();
    handleCameraOpen();
  };

  const handleSkip = () => {
    onPhotoChange(undefined);
    onNext();
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Foto Pagamento
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Scatta una foto del pagamento (scontrino, assegno, bonifico, ecc.) per documentare la donazione.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <FormCard
        title="Documentazione Pagamento"
        subtitle="Aggiungi una foto per documentare il pagamento"
      >
        {photo && previewUrl ? (
          <Box sx={{ width: '100%', maxWidth: '100%' }}>
            <Card sx={{ 
              width: '100%', 
              maxWidth: { xs: '100%', sm: 400 }, 
              mx: 'auto',
              overflow: 'hidden'
            }}>
              <CardMedia
                component="img"
                height="250"
                image={previewUrl}
                alt="Foto pagamento"
                sx={{ 
                  objectFit: 'cover',
                  width: '100%',
                  height: 'auto',
                  maxHeight: 250
                }}
              />
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <CheckIcon color="success" />
                  <Typography variant="subtitle2" color="success.main">
                    Foto acquisita
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                  {photo.name} ({(photo.size / 1024).toFixed(1)} KB)
                </Typography>
              </CardContent>
              <CardActions sx={{ 
                justifyContent: 'center', 
                gap: 1,
                flexWrap: 'wrap',
                px: 2,
                pb: 2
              }}>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={handleRetakePhoto}
                  size="small"
                  sx={{ minWidth: 'auto', flex: { xs: '1 1 auto', sm: '0 0 auto' } }}
                >
                  Rifai Foto
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleDeletePhoto}
                  size="small"
                  sx={{ minWidth: 'auto', flex: { xs: '1 1 auto', sm: '0 0 auto' } }}
                >
                  Elimina
                </Button>
              </CardActions>
            </Card>
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Box sx={{ mb: 3 }}>
              <PhotoCameraIcon sx={{ fontSize: 64, color: 'text.secondary' }} />
            </Box>
            <Typography variant="h6" gutterBottom>
              Aggiungi Foto Pagamento
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Scatta una foto o seleziona un'immagine dal dispositivo
            </Typography>
            
            <Box sx={{ 
              display: 'flex', 
              gap: 2, 
              justifyContent: 'center', 
              flexWrap: 'wrap',
              width: '100%',
              maxWidth: '100%'
            }}>
              <PrimaryButton
                startIcon={<CameraIcon />}
                onClick={() => fileInputRef.current?.click()}
                sx={{ 
                  minWidth: { xs: 'auto', sm: 200 },
                  flex: { xs: '1 1 auto', sm: '0 0 auto' },
                  maxWidth: { xs: '100%', sm: 'none' }
                }}
              >
                Carica Foto/File
              </PrimaryButton>
            </Box>
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
              multiple={false}
              capture="environment"
            />
          </Box>
        )}

        <Alert severity="info" sx={{ mt: 3 }}>
          <Typography variant="body2">
            <strong>Suggerimento:</strong> Assicurati che la foto sia ben leggibile e 
            contenga tutte le informazioni necessarie per documentare il pagamento.
            <br />
            <br />
            <strong>Su Safari:</strong> Il pulsante aprirà il file picker. Se vuoi scattare una foto, seleziona "Scatta foto" dal menu che appare.
          </Typography>
        </Alert>
      </FormCard>

      {/* Dialog Fotocamera */}
      <Dialog
        open={isCameraOpen}
        onClose={handleCameraClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { 
            bgcolor: 'black',
            width: '100%',
            maxWidth: '100%',
            margin: 1
          }
        }}
      >
        <DialogTitle sx={{ color: 'white', textAlign: 'center' }}>
          Fotocamera
        </DialogTitle>
        <DialogContent sx={{ p: 0, position: 'relative', width: '100%' }}>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            style={{
              width: '100%',
              height: 'auto',
              display: 'block',
              maxWidth: '100%',
              objectFit: 'cover'
            }}
          />
          <canvas
            ref={canvasRef}
            style={{ display: 'none' }}
          />
        </DialogContent>
        <DialogActions sx={{ 
          justifyContent: 'center', 
          p: 2, 
          bgcolor: 'black',
          flexWrap: 'wrap',
          gap: 2
        }}>
          <IconButton
            onClick={handleCameraClose}
            sx={{ 
              color: 'white', 
              mr: { xs: 0, sm: 2 },
              order: { xs: 2, sm: 1 }
            }}
          >
            <CloseIcon />
          </IconButton>
          <IconButton
            onClick={handleCapture}
            disabled={isCapturing}
            sx={{
              color: 'white',
              bgcolor: 'primary.main',
              '&:hover': { bgcolor: 'primary.dark' },
              width: { xs: 56, sm: 64 },
              height: { xs: 56, sm: 64 },
              order: { xs: 1, sm: 2 }
            }}
          >
            {isCapturing ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              <CameraIcon fontSize="large" />
            )}
          </IconButton>
        </DialogActions>
      </Dialog>

      {showNavigationButtons && (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mt: 4,
          flexWrap: 'wrap',
          gap: 2,
          width: '100%'
        }}>
          <BackButton onClick={onBack} />
          <Box sx={{ 
            display: 'flex', 
            gap: 2,
            flexWrap: 'wrap',
            justifyContent: { xs: 'center', sm: 'flex-end' },
            width: { xs: '100%', sm: 'auto' }
          }}>
            <SecondaryButton 
              onClick={handleSkip}
              sx={{ 
                minWidth: { xs: 'auto', sm: 'auto' },
                flex: { xs: '1 1 auto', sm: '0 0 auto' }
              }}
            >
              Salta
            </SecondaryButton>
            <NextButton
              onClick={onNext}
              disabled={!photo}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

